import { useEffect, useRef } from "react";
import { useAuth } from "../../../hooks/useAuth";

/**
 * Mantém o FCM token deste device fresco para quem JÁ optou por push.
 *
 * FCM rotaciona tokens; sem um re-registro periódico o push morre em
 * silêncio semanas depois do opt-in. Este componente roda uma vez por
 * sessão e só faz trabalho quando as três condições valem:
 *  - usuário logado com `notificationsOptIn === true` (setado pelo toggle de
 *    Ajustes ou pelo convite `PushPermissionSheet` — os dois únicos lugares
 *    que pedem permissão, sempre por clique), e
 *  - permissão do browser já `granted` (aqui NUNCA se pede permissão), e
 *  - o token atual do device ainda não está em `profile.fcmTokens`.
 *
 * Custo: o caminho comum (token já registrado) é resolvido com o profile
 * que o AuthContext já tem — zero reads extras. O import de
 * `firebase/messaging` é dinâmico e só acontece depois dos guards, então
 * visitante deslogado/sem opt-in não paga o chunk.
 */
export function FcmTokenSync() {
  const { user, profile } = useAuth();
  const syncedUidRef = useRef<string | null>(null);

  const uid = user?.uid ?? null;
  const optIn = profile?.notificationsOptIn === true;
  const knownTokens = profile?.fcmTokens;

  useEffect(() => {
    if (!uid || !optIn) return;
    if (syncedUidRef.current === uid) return;
    if (knownTokens === undefined) return; // profile ainda carregando

    let cancelled = false;
    (async () => {
      // A leitura de permissão é async porque no app nativo ela vem do
      // plugin — `Notification.permission` não existe no WKWebView, e o guard
      // síncrono antigo fazia este sync nunca rodar no app das lojas.
      const { fetchFcmToken, getNotificationPermissionState } = await import(
        "../../../lib/firebase/messaging"
      );
      const permission = await getNotificationPermissionState();
      if (cancelled || permission !== "granted") return;
      const token = await fetchFcmToken();
      if (cancelled || !token) return;
      syncedUidRef.current = uid;
      if (knownTokens.includes(token)) return; // já registrado — zero writes
      const { registerFcmToken } = await import(
        "../../../services/firebase/notificationsTokenService"
      );
      await registerFcmToken(uid, token);
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, optIn, knownTokens]);

  return null;
}
